export type PermissionResource =
  | 'user'
  | 'client'
  | 'clients'
  | 'dashboard'
  | 'widget'
  | 'log'
  | 'alert'
  | 'attack'
  | 'attack_note'
  | 'alert_rule'
  | 'alert_note'
  | 'anomaly'
  | 'anomaly_note'
  | 'product'
  | 'product_version'
  | 'device'
  | 'audit_log'
  | 'sdk'
  | 'api_key'
  | 'sso_configuration'
  | 'documentation'
  | 'dashboard_statistics';

export type PermissionAction = `${PermissionResource}.${string}`;

export type PermissionCondition = {
  field: string;
  operator: 'equal';
  value?: unknown;
};

export type PermissionPolicy = {
  action: PermissionAction;
  conditions?: PermissionCondition[];
};

export const CURRENT_USER_IDENTIFIER = 'current_user';
export const CURRENT_CLIENT_IDENTIFIER = 'current_client';

export const AllPermissionActions = {
  // users
  UserRead: 'user.read',
  UserInvite: 'user.invite',
  UserUpdate: 'user.update',
  UserDelete: 'user.delete',
  UserChangePassword: 'user.change_password',
  UserUpdateEmailSubscriptions: 'user.update_email_subscriptions',

  // clients
  ClientsRead: 'clients.read',
  ClientRead: 'client.read',
  ClientCreate: 'client.create',
  ClientUpdate: 'client.update',
  ClientDomainUpdate: 'client.domain_update',
  ClientDelete: 'client.delete',
  ClientMarkAsFavourite: 'client.mark_as_favourite',

  // sso
  SsoConfigurationActivate: 'sso_configuration.activate',
  SsoConfigurationRead: 'sso_configuration.read',
  SsoConfigurationUpdate: 'sso_configuration.update',

  // dashboards
  DashboardRead: 'dashboard.read',
  DashboardCreate: 'dashboard.create',
  DashboardUpdate: 'dashboard.update',
  DashboardDelete: 'dashboard.delete',

  // widgets
  WidgetRead: 'widget.read',
  WidgetCreate: 'widget.create',
  WidgetUpdate: 'widget.update',
  WidgetDelete: 'widget.delete',
  WidgetSetReadOnly: 'widget.set_readonly',
  WidgetUnsetReadOnly: 'widget.unset_readonly',
  WidgetExport: 'widget.export',

  // logs
  LogRead: 'log.read',
  LogExport: 'log.export',

  // alerts
  AlertRead: 'alert.read',
  AlertResolve: 'alert.resolve',
  AlertExport: 'alert.export',
  AlertRuleRead: 'alert_rule.read',
  AlertRuleCreate: 'alert_rule.create',
  AlertRuleUpdate: 'alert_rule.update',
  AlertRuleDelete: 'alert_rule.delete',
  AlertNoteRead: 'alert_note.read',
  AlertNoteCreate: 'alert_note.create',
  AlertNoteUpdate: 'alert_note.update',
  AlertNoteDelete: 'alert_note.delete',

  // attacks
  AttackRead: 'attack.read',
  AttackResolve: 'attack.resolve',
  AttackExport: 'attack.export',
  AttackNoteRead: 'attack_note.read',
  AttackNoteCreate: 'attack_note.create',
  AttackNoteUpdate: 'attack_note.update',
  AttackNoteDelete: 'attack_note.delete',

  // anomalies
  AnomalyRead: 'anomaly.read',
  AnomalyResolve: 'anomaly.resolve',
  AnomalyNoteRead: 'anomaly_note.read',
  AnomalyNoteCreate: 'anomaly_note.create',
  AnomalyNoteUpdate: 'anomaly_note.update',
  AnomalyNoteDelete: 'anomaly_note.delete',

  // products
  ProductRead: 'product.read',
  ProductCreate: 'product.create',
  ProductUpdate: 'product.update',
  ProductDelete: 'product.delete',

  // product versions
  ProductVersionRead: 'product_version.read',
  ProductVersionCreate: 'product_version.create',
  ProductVersionUpdate: 'product_version.update',
  ProductVersionDelete: 'product_version.delete',
  ProductVersionLock: 'product_version.lock',
  ProductVersionUnlock: 'product_version.unlock',
  ProductVersionClearData: 'product_version.clear_data',

  // devices
  DeviceExport: 'device.export',

  // documentation
  DocumentationCenterLink: 'documentation.center_link',

  // dashboard statistics
  DashboardStatisticsTotalUsers: 'dashboard_statistics.total_users',

  // audit logs
  AuditLogRead: 'audit_log.read',
  AuditLogExport: 'audit_log.export',

  // others
  SDKRead: 'sdk.read',
  APIKeyRead: 'api_key.read',
  APIKeyCreate: 'api_key.create',
  APIKeyUpdate: 'api_key.update',
  APIKeyDelete: 'api_key.delete',
} as const;
